import { Link } from "react-router-dom";
import { useReveal } from "../lib/useReveal";
import { HardHat, School, Users, Droplets, Sparkles, Wrench, GraduationCap, ArrowRight, MapPin } from "lucide-react";

const PHOTOS = [
  { src: "/images/halegere-1.jpg", alt: "Parinitha Aided Higher Primary School, Halegere — main building" },
  { src: "/images/halegere-2.jpg", alt: "Halegere school playground and equipment — current state" },
  { src: "/images/halegere-3.jpg", alt: "Halegere school water tank, washroom and play area — works in progress" },
];

const WORKS = [
  { icon: School,        title: "Three new classrooms",         detail: "Being built for Standards 8, 9 and 10." },
  { icon: Droplets,      title: "25 child-friendly toilets",    detail: "10 for boys, 10 for girls, plus separate toilets for teachers." },
  { icon: Sparkles,      title: "20-ft handwash sink",          detail: "A central sanitation block with running water." },
  { icon: Droplets,      title: "RO water purifier",            detail: "250-student capacity with two years of maintenance included." },
  { icon: Wrench,        title: "Playground development",       detail: "Ground formation, and repair &amp; repainting of existing play equipment." },
  { icon: GraduationCap, title: "Salary support for 2 teachers", detail: "Ensuring consistent, quality teaching for a full academic year." },
  { icon: Users,         title: "Salary support for 2 attenders", detail: "Supporting the non-teaching staff who keep the school running." },
];

const inr = () => "";

export default function WorksInProgress() {
  const ref = useReveal();

  return (
    <section id="works" data-testid="works-section" className="relative py-16 md:py-32 bg-cream-soft">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={ref} className="reveal flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 items-center">
          <div className="max-w-3xl text-center md:text-left min-w-0">
            <span className="section-chip section-chip-coral">
              <HardHat className="w-3.5 h-3.5 mr-1.5" />
              Works Under Progress
            </span>
            <h2 className="mt-4 font-display font-extrabold text-navy text-3xl sm:text-4xl md:text-6xl leading-[1.05] tracking-tight">
              Halegere School &mdash; <em>Our Current Project.</em>
            </h2>
            <div className="mt-3 flex flex-wrap items-center justify-center md:justify-start gap-x-6 gap-y-2 text-navy/75 font-display">
              <div className="flex items-start gap-2 text-sm md:text-base text-left">
                <MapPin className="w-4 h-4 text-teal mt-1 shrink-0" />
                <span>Halegere Village, Basaralu Hobli, Mandya Taluk &amp; District &mdash; 571446, Karnataka</span>
              </div>
            </div>
            <p className="mt-4 text-navy/75 font-display text-base md:text-lg leading-relaxed">
              <strong>Parinitha Aided Higher Primary School</strong> is the school Akshara Daana is currently
              rebuilding. Three new classrooms, hygienic toilets, RO drinking water, a revived playground and
              salary support for teachers &mdash; a full-scope adoption in motion.
            </p>
          </div>
          <Link to="/get-involved#donate" data-testid="works-cta" className="btn-vermilion shrink-0">
            Fund the Halegere works
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Photo strip */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          {PHOTOS.map((p, i) => (
            <figure key={p.src} data-testid={`works-photo-${i}`} className="relative overflow-hidden bg-navy/10 aspect-[4/3] shadow-card group">
              <img src={p.src} alt={p.alt} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <figcaption className="absolute bottom-3 left-3 bg-navy/85 text-white font-display font-semibold text-xs px-3 py-1.5 rounded-sm">
                {i === 0 ? "Main Building" : i === 1 ? "School Grounds" : "Sanitation & Play Area"}
              </figcaption>
            </figure>
          ))}
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Works list */}
          <div className="lg:col-span-8 min-w-0">
            <div className="bg-white border border-navy/10 rounded-sm shadow-card">
              <div className="flex flex-wrap items-center justify-between gap-2 px-5 sm:px-6 md:px-8 py-5 border-b border-navy/10">
                <h3 className="font-display font-extrabold text-navy text-lg sm:text-xl md:text-2xl">
                  What we&rsquo;re doing at Halegere
                </h3>
                <span className="text-[10px] uppercase tracking-widest font-display font-bold text-teal whitespace-nowrap">Live · Under Progress</span>
              </div>
              <ul>
                {WORKS.map((w) => {
                  const Icon = w.icon;
                  return (
                    <li
                      key={w.title}
                      data-testid={`works-item-${w.title.replace(/\s+/g, "-").toLowerCase()}`}
                      className="flex items-start gap-4 px-5 sm:px-6 md:px-8 py-4 border-b border-navy/10 last:border-b-0 hover:bg-cream-soft transition-colors"
                    >
                      <div className="w-10 h-10 rounded-full bg-teal/10 text-teal flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-bold text-navy">{w.title}</div>
                        <div className="text-navy/65 font-display text-sm" dangerouslySetInnerHTML={{ __html: w.detail }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {/* Sidebar callout */}
          <aside className="lg:col-span-4 space-y-6 min-w-0">
            <div className="bg-teal text-white p-6 sm:p-7 rounded-sm">
              <div className="text-[10px] uppercase tracking-widest text-white/85 font-display font-bold">
                Every drop makes an ocean
              </div>
              <h4 className="mt-3 font-display font-extrabold text-2xl leading-tight">
                Adopt a piece of Halegere.
              </h4>
              <p className="mt-3 font-display text-white/90 text-sm leading-relaxed">
                One classroom. One toilet block. One year of a teacher&rsquo;s salary. Pick any line item above
                and fund it in full &mdash; we will name it in your honour and report progress every quarter.
              </p>
              <Link
                to="/get-involved#donate"
                className="mt-5 inline-flex items-center gap-2 bg-white text-teal font-display font-bold text-sm px-5 py-3 rounded-sm hover:bg-cream transition-colors"
              >
                Contribute now
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="bg-white border border-navy/10 p-6 rounded-sm shadow-card">
              <div className="text-[10px] uppercase tracking-widest text-coral font-display font-bold">Prime Donor</div>
              <div className="mt-2 font-display font-extrabold text-navy text-lg leading-tight">
                Dr Vedavathi and Lakshman Agadi Foundation NFP
              </div>
              <p className="mt-1 font-display text-navy/70 text-sm">
                The founding supporter of the Halegere school project.
              </p>
            </div>

            <div className="bg-white border border-navy/10 p-6 rounded-sm shadow-card">
              <div className="text-[10px] uppercase tracking-widest text-teal font-display font-bold">Tax Benefits</div>
              <ul className="mt-3 space-y-2 font-display text-navy/80 text-sm">
                <li>&bull; 80G Tax Exemption (India)</li>
                <li>&bull; 501(c)(3) Tax-Exempt Donation (USA)</li>
                <li>&bull; FCRA approved &mdash; in support with AarogyaSeva Foundation</li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
